import React from 'react';
import * as yup from 'yup';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormLabel from '@mui/material/FormLabel';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

import { logger } from '../../logging';
import { FORM_TITLES } from '../constants';
import {
  ExcelUploadField,
  SubmissionButton,
} from '../../components';
import { jarvisApi } from '../../api';
import { useAuth } from '../../auth';
import { useFullFormatDetection } from '../../hooks';
import { CONTAINERS } from '../../../../shared-jarvis';
import {
  CarrierListField,
  EnterpriseSearchField,
  FileNameField,
  NotifyEmailListField,
} from '../fields';
import { batchFieldSchema } from '../batchFieldSchema';
import { batchRequestColumnBuilders } from './workbookColumnBuilders';
import {
  withUploadToBlobStorage,
  WithUploadToBlobStorageProps,
} from '../withUploadToBlobStorage';

const schema = yup.object({
  ...batchFieldSchema,
});

export type BatchRateFormFields = yup.InferType<typeof schema>;

const Form = ({
  handleFormSubmit,
  submitting,
}: WithUploadToBlobStorageProps<BatchRateFormFields>) => {
  const auth = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BatchRateFormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      returnCustomerRates: true,
      returnLiveContractRates: true,
      returnProposalContractRates: false,
      returnVolumeRates: false,
      accounts: [],
      targetedCarriers: [],
      notifyEmails: auth.user?.email ? [{ value: auth.user.email }] : [],
    },
    shouldUnregister: true,
  });

  const { loadingFromFile, loadedFromFile } = useFullFormatDetection(watch, setValue, {
    isBGEmployee: auth.isBGEmployee,
    enterpriseID: auth.user?.enterpriseID,
  });

  const onSubmit: SubmitHandler<BatchRateFormFields> = async (formData) => {
    handleFormSubmit(
      formData,
      CONTAINERS.RATEQUALITY_SHIPMENTS_BATCH,
      batchRequestColumnBuilders
    );
  };

  return (
    <>
      <Typography
        align="center"
        component="h1"
        variant="h4"
        sx={{ mb: 3, mt: 2 }}
      >
        {FORM_TITLES.BATCH_REQUEST}
      </Typography>

      <Typography align="center">
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={async () => {
            try {
              await jarvisApi.fetchBatchRateTemplate();
              logger.info('Download Success: Batch Rate Template');
            } catch (e: any) {
              logger.error('Download Error: Batch Rate Template', {
                error: { message: e.message, stack: e.stack },
              });
            }
          }}
        >
          Download Batch Rate Template
        </Button>
      </Typography>

      <form
        style={{ marginTop: '1rem', marginBottom: 0 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FileNameField<BatchRateFormFields>
          control={control}
          name="fileName"
          error={errors.fileName}
        />

        <ExcelUploadField
          label="Shipment Data"
          name="shipmentsFileUpload"
          control={control}
          error={errors.shipmentsFileUpload}
        />

        {loadingFromFile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Reading configuration from file...
            </Typography>
          </Box>
        )}

        {loadedFromFile && !loadingFromFile && (
          <Chip
            label="Configuration loaded from uploaded file — review and adjust as needed"
            color="success"
            size="small"
            variant="outlined"
            sx={{ mt: 1 }}
          />
        )}

        <EnterpriseSearchField<BatchRateFormFields>
          control={control}
          error={errors.accounts as any}
          helperText="Search by Account Name or Number (select up to 4 accounts)"
          label="Rating Accounts"
          multiple={true}
          name="accounts"
          user={auth.user!}
        />

        <Box sx={{ mt: 3, mb: 2 }}>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            Rate Return Options
          </FormLabel>
          <FormGroup>
            <Controller
              name="returnCustomerRates"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Return Customer Rates"
                />
              )}
            />
            <Controller
              name="returnLiveContractRates"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Return Live Contract Rates"
                />
              )}
            />
            <Controller
              name="returnProposalContractRates"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Return Proposal Contract Rates"
                />
              )}
            />
            <Controller
              name="returnVolumeRates"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Return Volume Rates"
                />
              )}
            />
          </FormGroup>
        </Box>

        <CarrierListField<BatchRateFormFields>
          control={control}
          error={errors.targetedCarriers}
          label="Targeted Carriers (Optional)"
          name="targetedCarriers"
        />

        <NotifyEmailListField<BatchRateFormFields>
          control={control}
          error={errors.notifyEmails}
          name="notifyEmails"
          hidden={!auth.isBGEmployee}
        />

        <SubmissionButton
          loading={submitting}
          buttonProps={{
            sx: { mt: 3, p: 2 },
            fullWidth: true,
            type: 'submit',
          }}
        />
      </form>
    </>
  );
};

export const BatchRateForm = withUploadToBlobStorage(Form);
